/**
 * lib/click-context-decode.ts — READ a click-context token without verifying it.
 *
 * Browser-safe half of lib/click-context.ts (FRIDGE-818): no node:crypto, so
 * the product page can label its own analytics events with the channel the
 * visitor arrived from. Anything decoded here is UNVERIFIED — a page cannot
 * hold the secret — so every consumer marks it `channel_verified: false`.
 * The verified record is the /r/ click row, written by the server.
 */

/** The query parameter the token rides in. */
export const CLICK_CONTEXT_PARAM = 'cx'

export interface ClickContext {
  channel: string
  surface: string
  tool: string
  position: number | null
  fin: string | null
  /** The `<session>.<call>` trace of the tool call that minted the link (FRIDGE-839), when known. */
  trace: string | null
  /** Unix seconds when the token was minted. */
  issuedAt: number
}

const TOKEN_SHAPE = /^[A-Za-z0-9_-]{16,400}\.[a-f0-9]{32}$/

/** Is this string even token-shaped? Cheap gate before any decoding. */
export function looksLikeClickContext(v: unknown): v is string {
  return typeof v === 'string' && TOKEN_SHAPE.test(v)
}

function fromBase64url(s: string): string | null {
  try {
    // Node 24 and browsers share this path; Buffer pulls a polyfill into public pages.
    const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (s.length % 4)) % 4)
    return decodeURIComponent(
      Array.from(atob(b64), (c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0')).join('')
    )
  } catch {
    return null
  }
}

/** Decode the payload half of a token. Null for anything not well-formed. */
export function decodeClickContextUnverified(token: string | null | undefined): ClickContext | null {
  if (!looksLikeClickContext(token)) return null
  const json = fromBase64url(token.slice(0, token.indexOf('.')))
  if (!json) return null
  try {
    const o = JSON.parse(json) as Record<string, unknown>
    if (o.v !== 1 || typeof o.c !== 'string' || typeof o.s !== 'string' || typeof o.t !== 'string' || typeof o.i !== 'number') {
      return null
    }
    return {
      channel: o.c,
      surface: o.s,
      tool: o.t,
      position: typeof o.p === 'number' ? o.p : null,
      fin: typeof o.f === 'string' ? o.f : null,
      trace: typeof o.r === 'string' ? o.r : null,
      issuedAt: o.i,
    }
  } catch {
    return null
  }
}

/**
 * The token on the current page, if the visitor arrived with one. Browser
 * only; null on the server. Reads through globalThis so this module also
 * compiles under the MCP package's DOM-less tsconfig.
 */
export function readClickContextFromLocation(): string | null {
  const w = (globalThis as { window?: { location?: { search?: string } } }).window
  if (!w?.location) return null
  try {
    const v = new URLSearchParams(w.location.search ?? '').get(CLICK_CONTEXT_PARAM)
    return looksLikeClickContext(v) ? v : null
  } catch {
    return null
  }
}
